import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { EloChange } from '../common';
import Button from '../common/Button';
import { formatPlayerName, formatPlayerNameCompact } from '../../utils/playerUtils';
import { supabase } from '../../utils/supabase';
import GameContextLinks from '../games/GameContextLinks';
import { getGameContextsCountsBatch } from '../../services/gameContextsService';
import {
  fetchScoreCorrectionMetaBySessionIds,
  sessionGameIdFromHistoryRow,
} from '../../services/scoreCorrectionMetaService';
import GameHistoryFilters from './GameHistoryFilters';
import ScoreCorrectedBadge from '../games/ScoreCorrectedBadge';
import { TEAM_WIN, TEAM_LOSS, TEAM_NEUTRAL, teamHighlightClass } from '../games/gameHistoryRowStyles';

const YOUR_TEAM_WIN = TEAM_WIN;
const YOUR_TEAM_LOSS = TEAM_LOSS;
const OPPONENT_NEUTRAL = TEAM_NEUTRAL;

/**
 * Component to display a paginated table of a player's game history
 * @param {Array} games - Array of game objects
 * @param {number} pageSize - Number of games per page
 * @param {boolean} hideDateColumn - When true, hides the per-row Date column (session group header already shows the date).
 * @param {boolean} hideSessionColumn - @deprecated Prefer hideDateColumn. Same behavior — hides Date column when session context/header shows it.
 * @param {boolean} hideFilters - If true, hides the filter controls (useful when already in a filtered context like session performance)
 * @param {boolean} hideViewSessionLink - If true, hides "View Session" on session group headers (e.g. already on session performance)
 * @param {boolean} hideSessionHeaders - If true, hides session group title rows (e.g. session performance hero already shows session/club/date)
 * @param {boolean} [hideHeading=false] - When true, omit the default "Game History" heading (parent supplies context)
 * @param {string} clubId - Optional club ID for visibility filtering
 * @param {boolean} latestSessionOnlyByDefault - When true, only games from the most recent session until user expands
 * @param {string} [subjectPlayerLabel='You'] - Label for the focal player row (use their name when viewing another player)
 * @param {'player'|'sessionTeams'} [rowLayout='player'] - player = You+teammates vs opponents; sessionTeams = full court team vs team
 * @param {boolean} [showScoreCorrectionBadge=true] - Show an “Edited” badge on score-corrected games
 */
const GameHistoryTable = ({
  games = [],
  pageSize = 10,
  hideSessionColumn = false,
  hideDateColumn,
  hideFilters = false,
  hideViewSessionLink = false,
  hideSessionHeaders = false,
  hideHeading = false,
  clubId = null,
  latestSessionOnlyByDefault = false,
  subjectPlayerLabel = 'You',
  rowLayout = 'player',
  showScoreCorrectionBadge = true,
  hideEloRatings = false,
}) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [expandedGameId, setExpandedGameId] = useState(null);
  const [linkCounts, setLinkCounts] = useState({});
  const [correctionMetaByGameId, setCorrectionMetaByGameId] = useState(() => new Map());
  const [showFullHistory, setShowFullHistory] = useState(!latestSessionOnlyByDefault);
  /** Whole section collapsed (saves scroll); open by default so latest-session games are visible. */
  const [sectionOpen, setSectionOpen] = useState(true);
  const [filters, setFilters] = useState({
    clubId: null,
    hasLink: null, // null = all, true = has link, false = no link
    result: null, // null = all, 'win' = wins, 'loss' = losses
    dateRange: null // null = all, 'today', 'this_week', 'this_month', 'last_30_days', 'last_90_days', 'this_year'
  });

  const sessionIdsKey = useMemo(() => {
    const ids = new Set(
      (games || []).map((g) => g.session_games?.session_id || g.session_id).filter(Boolean)
    );
    return [...ids].sort().join(',');
  }, [games]);

  const latestSessionId = useMemo(() => {
    if (!games?.length) return null;
    const sorted = [...games].sort((a, b) => {
      const da = new Date(a.created_at || a.session_games?.created_at || 0);
      const db = new Date(b.created_at || b.session_games?.created_at || 0);
      return db - da;
    });
    return sorted[0]?.session_games?.session_id || sorted[0]?.session_id || null;
  }, [games]);

  const hasMultipleSessions = useMemo(() => {
    const ids = new Set(
      (games || []).map((g) => g.session_games?.session_id || g.session_id).filter(Boolean)
    );
    return ids.size > 1;
  }, [games]);

  const gamesForProcessing = useMemo(() => {
    if (!games?.length) return [];
    if (!latestSessionOnlyByDefault || showFullHistory || !latestSessionId) return games;
    return games.filter(
      (g) => (g.session_games?.session_id || g.session_id) === latestSessionId
    );
  }, [games, latestSessionOnlyByDefault, showFullHistory, latestSessionId]);

  useEffect(() => {
    if (latestSessionOnlyByDefault) {
      setShowFullHistory(false);
      setCurrentPage(1);
    }
  }, [sessionIdsKey, latestSessionOnlyByDefault]);

  useEffect(() => {
    if (latestSessionOnlyByDefault) {
      setCurrentPage(1);
    }
  }, [showFullHistory, latestSessionOnlyByDefault]);

  useEffect(() => {
    // Get current user
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setCurrentUserId(user.id);
      }
    };
    getCurrentUser();
  }, []);

  useEffect(() => {
    if (!showScoreCorrectionBadge || !sessionIdsKey) {
      setCorrectionMetaByGameId(new Map());
      return;
    }
    let cancelled = false;
    const sessionIds = sessionIdsKey.split(',').filter(Boolean);
    fetchScoreCorrectionMetaBySessionIds(sessionIds).then((meta) => {
      if (!cancelled) setCorrectionMetaByGameId(meta);
    });
    return () => {
      cancelled = true;
    };
  }, [sessionIdsKey, showScoreCorrectionBadge]);

  // Format player name with gender-based styling
  const formatPlayerNameLocal = (player) => {
    return formatPlayerName(player, 'body-small');
  };

  const renderEditedBadge = (game) => {
    if (!showScoreCorrectionBadge) return null;
    const gameId = sessionGameIdFromHistoryRow(game);
    const meta = gameId ? correctionMetaByGameId.get(gameId) : null;
    if (!meta) return null;
    return (
      <ScoreCorrectedBadge
        correctedAt={meta.correctedAt}
        correctionMode={meta.correctionMode}
      />
    );
  };

  // Get unique clubs from games for filter dropdown
  const uniqueClubs = useMemo(() => {
    const clubsMap = new Map();
    gamesForProcessing.forEach((game) => {
      const clubId = game.club_id || game.session_games?.club_id;
      const clubName = game.clubName || game.club_name;
      if (clubId && clubName && !clubsMap.has(clubId)) {
        clubsMap.set(clubId, { id: clubId, name: clubName });
      }
    });
    return Array.from(clubsMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [gamesForProcessing]);

  // Helper function to check if game date matches date range filter
  const matchesDateRange = (gameDate, dateRange) => {
    if (!dateRange) return true;
    
    const gameDateTime = new Date(gameDate);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (dateRange) {
      case 'today':
        const gameDateOnly = new Date(gameDateTime.getFullYear(), gameDateTime.getMonth(), gameDateTime.getDate());
        return gameDateOnly.getTime() === today.getTime();
      
      case 'this_week':
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return gameDateTime >= weekAgo;
      
      case 'this_month':
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return gameDateTime >= monthStart;
      
      case 'last_30_days':
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        return gameDateTime >= thirtyDaysAgo;
      
      case 'last_90_days':
        const ninetyDaysAgo = new Date(today);
        ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
        return gameDateTime >= ninetyDaysAgo;
      
      case 'this_year':
        const yearStart = new Date(now.getFullYear(), 0, 1);
        return gameDateTime >= yearStart;
      
      default:
        return true;
    }
  };

  // Apply filters to games (excluding hasLink filter for now - will apply after link counts load)
  const filteredGamesWithoutLinkFilter = useMemo(() => {
    if (!gamesForProcessing || gamesForProcessing.length === 0) return [];

    return gamesForProcessing.filter((game) => {
      // Club filter
      if (filters.clubId) {
        const gameClubId = game.club_id || game.session_games?.club_id;
        if (gameClubId !== filters.clubId) return false;
      }
      
      // Result filter
      if (filters.result) {
        const isWin = game.result === 'win' || 
                     (game.winning_team && game.team_number === game.winning_team);
        if (filters.result === 'win' && !isWin) return false;
        if (filters.result === 'loss' && isWin) return false;
      }
      
      // Date range filter
      if (filters.dateRange) {
        const gameDate = game.created_at || game.session_games?.created_at;
        if (!gameDate || !matchesDateRange(gameDate, filters.dateRange)) return false;
      }
      
      return true;
    });
  }, [gamesForProcessing, filters.clubId, filters.result, filters.dateRange]);

  // Apply hasLink filter after link counts are available
  const filteredGames = useMemo(() => {
    if (filters.hasLink === null) {
      return filteredGamesWithoutLinkFilter;
    }
    
    return filteredGamesWithoutLinkFilter.filter(game => {
      const hasLink = linkCounts[game.id] > 0;
      return filters.hasLink === hasLink;
    });
  }, [filteredGamesWithoutLinkFilter, filters.hasLink, linkCounts]);

  // Group games by session and calculate game numbers within each session
  const gamesWithNumbers = useMemo(() => {
    if (!filteredGames || filteredGames.length === 0) return [];
    
    // Group games by session
    const sessionGroups = {};
    filteredGames.forEach(game => {
      const sessionId = game.session_games?.session_id || game.session_id;
      const key = sessionId || 'unknown';
      if (!sessionGroups[key]) {
        sessionGroups[key] = [];
      }
      sessionGroups[key].push(game);
    });
    
    // Sort games within each session by created_at and assign game numbers
    const gamesWithNumbers = [];
    Object.values(sessionGroups).forEach(sessionGames => {
      // Sort by created_at to get chronological order
      const sorted = [...sessionGames].sort((a, b) => {
        const dateA = new Date(a.created_at || a.session_games?.created_at || 0);
        const dateB = new Date(b.created_at || b.session_games?.created_at || 0);
        return dateA - dateB;
      });
      
      // Assign game numbers (1, 2, 3, etc.) within this session
      sorted.forEach((game, index) => {
        gamesWithNumbers.push({
          ...game,
          gameNumber: index + 1
        });
      });
    });
    
    // Sort all games by date (most recent first) while preserving game numbers
    return gamesWithNumbers.sort((a, b) => {
      const dateA = new Date(a.created_at || a.session_games?.created_at || 0);
      const dateB = new Date(b.created_at || b.session_games?.created_at || 0);
      return dateB - dateA; // Most recent first
    });
  }, [filteredGames]);

  // Calculate pagination
  const totalPages = Math.ceil(gamesWithNumbers.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedGames = gamesWithNumbers.slice(startIndex, startIndex + pageSize);

  // Memoize paginated games IDs to prevent unnecessary re-renders
  const paginatedGameIds = useMemo(() => paginatedGames.map(g => g.id), [paginatedGames]);

  // Calculate session groups once - memoized (moved out of render loop)
  const sessionGroups = useMemo(() => {
    const groups = [];
    let currentGroup = 0;
    paginatedGames.forEach((g, idx) => {
      const sessId = g.session_games?.session_id || g.session_id;
      if (idx > 0) {
        const prevSessId = paginatedGames[idx - 1]?.session_games?.session_id || paginatedGames[idx - 1]?.session_id;
        if (sessId !== prevSessId) {
          currentGroup++;
        }
      }
      groups[idx] = currentGroup;
    });
    return groups;
  }, [paginatedGames]);

  // Memoize formatted dates to avoid re-formatting on every render
  const formattedDates = useMemo(() => {
    const dates = {};
    paginatedGames.forEach(game => {
      dates[game.id] = format(new Date(game.created_at || game.session_games?.created_at || 0), 'MMM d, yyyy');
    });
    return dates;
  }, [paginatedGames]);

  // Load link counts for all filtered games (not just current page) when hasLink filter is active
  // Otherwise, only load for current page for performance
  useEffect(() => {
    const loadLinkCounts = async () => {
      if (!currentUserId) return;
      
      // If hasLink filter is active, load counts for all filtered games
      // Otherwise, just load for current page
      const gamesToLoad = filters.hasLink !== null 
        ? filteredGamesWithoutLinkFilter 
        : paginatedGames;
      
      if (gamesToLoad.length === 0) return;
      
      // Build participants map for batch processing
      const gameParticipantsMap = {};
      gamesToLoad.forEach(game => {
        if (rowLayout === 'sessionTeams') {
          gameParticipantsMap[game.id] = [
            ...(game.team1Players?.map((p) => p?.id).filter(Boolean) || []),
            ...(game.team2Players?.map((p) => p?.id).filter(Boolean) || []),
          ];
        } else {
          gameParticipantsMap[game.id] = [
            ...(game.teammates?.map(t => t?.id).filter(Boolean) || []),
            ...(game.opponents?.map(o => o?.id).filter(Boolean) || []),
            game.player_id
          ].filter(Boolean);
        }
      });
      
      try {
        const gameIds = gamesToLoad.map(g => g.id);
        const counts = await getGameContextsCountsBatch(
          gameIds,
          currentUserId,
          clubId,
          gameParticipantsMap
        );
        
        // Merge with existing counts to avoid losing data
        setLinkCounts(prev => ({ ...prev, ...counts }));
      } catch (err) {
        console.error('Error loading link counts:', err);
        // Set all to 0 on error for these games
        const zeroCounts = {};
        gamesToLoad.forEach(game => {
          zeroCounts[game.id] = 0;
        });
        setLinkCounts(prev => ({ ...prev, ...zeroCounts }));
      }
    };
    
    loadLinkCounts();
  }, [
    filters.hasLink !== null ? filteredGamesWithoutLinkFilter.map(g => g.id).join(',') : paginatedGameIds.join(','),
    currentUserId,
    clubId,
    rowLayout,
  ]);

  // Filter handler functions
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value === '' || value === 'all' ? null : value
    }));
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const clearFilters = () => {
    setFilters({
      clubId: null,
      hasLink: null,
      result: null,
      dateRange: null
    });
    setCurrentPage(1);
  };

  const hasActiveFilters = filters.clubId || filters.hasLink !== null || filters.result || filters.dateRange;

  // Early return after all hooks
  if (!games || games.length === 0) {
    return (
      <div className="flex justify-center items-center h-24 bg-surface-subtle rounded-lg border border-border">
        <p className="text-text-muted">No game history available.</p>
      </div>
    );
  }

  if (filteredGames.length === 0 && hasActiveFilters) {
    return (
      <div className="bg-surface rounded-xl shadow-sm border border-border-medium p-3 sm:p-6">
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-xl font-bold text-text-primary">Game History</h2>
          {!hideFilters && (
            <Button onClick={clearFilters} variant="link" size="small">
              Clear filters
            </Button>
          )}
        </div>
        <GameHistoryFilters
          hideFilters={hideFilters}
          filters={filters}
          uniqueClubs={uniqueClubs}
          handleFilterChange={handleFilterChange}
          hasActiveFilters={hasActiveFilters}
        />
        <div className="flex h-24 items-center justify-center rounded-lg border border-border bg-surface-subtle">
          <p className="text-text-muted">No games match the selected filters.</p>
        </div>
      </div>
    );
  }

  // Handle page changes
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  /** Compact + truncate on xs single-row; full names with wrap when stacked on mobile */
  const renderPartnerName = (player, { wrap = false } = {}) => {
    if (wrap) {
      return formatPlayerNameLocal(player);
    }
    return (
      <>
        <span className="truncate min-w-0 sm:hidden">
          {formatPlayerNameCompact(player, { className: 'text-xs' })}
        </span>
        <span className="truncate min-w-0 hidden sm:inline">
          {formatPlayerNameLocal(player)}
        </span>
      </>
    );
  };

  /** Pre-game team average rating (from elo_history old_value at match time). */
  const renderTeamAvgElo = (avgElo, game) => {
    if (hideEloRatings || avgElo == null || game.game_type === 'social') return null;
    return (
      <span
        className="shrink-0 tabular-nums text-[11px] font-medium text-text-muted sm:text-xs"
        title="Team rating at start of game"
      >
        ({avgElo})
      </span>
    );
  };

  const renderSessionTeamPlayers = (players, eloByPlayerId, game, { wrap = false } = {}) => {
    if (!players?.length) {
      return <span className="text-text-muted">—</span>;
    }
    return players.map((player, idx) => (
      <React.Fragment key={player.id || idx}>
        {idx > 0 ? <span className="shrink-0 text-text-muted">+</span> : null}
        {renderPartnerName(player, { wrap })}
        {eloByPlayerId?.[player.id] != null && !hideEloRatings && game.game_type !== 'social' && (
          <span className="shrink-0 ml-0.5">
            <EloChange change={eloByPlayerId[player.id]} showSign size="xs" className={wrap ? '' : 'sm:text-sm'} />
          </span>
        )}
      </React.Fragment>
    ));
  };

  const historySummarySubtitle =
    latestSessionOnlyByDefault && games?.length
      ? hasMultipleSessions && !showFullHistory
        ? `${gamesForProcessing.length} game${gamesForProcessing.length !== 1 ? 's' : ''} · Latest session`
        : `${filteredGames.length} game${filteredGames.length !== 1 ? 's' : ''} · ${
            showFullHistory ? 'All sessions' : 'Latest session'
          }`
      : null;

  return (
    <div
      className={`bg-surface rounded-xl border border-border-medium shadow-sm ${
        latestSessionOnlyByDefault ? 'overflow-hidden' : 'p-3 sm:p-6'
      }`}
    >
      {latestSessionOnlyByDefault && (
        <button
          type="button"
          className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-surface-subtle/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary sm:px-5"
          onClick={() => setSectionOpen((o) => !o)}
          aria-expanded={sectionOpen}
        >
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-bold text-text-primary sm:text-xl">Game history</h2>
            {historySummarySubtitle && (
              <p className="mt-0.5 text-xs text-text-secondary">{historySummarySubtitle}</p>
            )}
          </div>
          <svg
            className={`h-5 w-5 shrink-0 text-text-muted transition-transform ${sectionOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      )}

      {(!latestSessionOnlyByDefault || sectionOpen) && (
        <div className={latestSessionOnlyByDefault ? 'border-t border-border p-3 sm:p-5' : ''}>
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            {!latestSessionOnlyByDefault && !hideHeading && (
              <h2 className="text-xl font-bold text-text-primary">Game History</h2>
            )}
            {!hideFilters && hasActiveFilters && (
              <Button
                onClick={clearFilters}
                variant="link"
                size="small"
                className={latestSessionOnlyByDefault ? 'sm:ml-auto' : ''}
              >
                Clear filters
              </Button>
            )}
          </div>

          <GameHistoryFilters
            hideFilters={hideFilters}
            filters={filters}
            uniqueClubs={uniqueClubs}
            handleFilterChange={handleFilterChange}
            hasActiveFilters={hasActiveFilters}
          />

      {/* Game list — unified card layout (all screen sizes) */}
      <div className="space-y-3">
        {paginatedGames.map((game, index) => {
          if (rowLayout === 'sessionTeams') {
            const team1Players = game.team1Players || [];
            const team2Players = game.team2Players || [];
            const team1Score = game.session_games?.team_1_score ?? 0;
            const team2Score = game.session_games?.team_2_score ?? 0;
            const team1Won = team1Score > team2Score;
            const team2Won = team2Score > team1Score;
            const highlightTeam = game.highlightTeamNumber ?? null;
            const team1Class = teamHighlightClass(highlightTeam === 1, team1Won);
            const team2Class = teamHighlightClass(highlightTeam === 2, team2Won);
            const eloByPlayerId = game.eloByPlayerId || {};
            const date =
              formattedDates[game.id] ||
              format(new Date(game.created_at || game.session_games?.created_at || 0), 'MMM d, yyyy');
            const currentSessionId = game.session_games?.session_id || game.session_id;
            const prevGame = index > 0 ? paginatedGames[index - 1] : null;
            const prevSessionId = prevGame?.session_games?.session_id || prevGame?.session_id;
            const isNewSession = index === 0 || (index > 0 && currentSessionId !== prevSessionId);
            const sessionGroup = sessionGroups[index];
            const isEvenSession = sessionGroup % 2 === 0;
            const stableParticipantIds = [
              ...new Set([
                ...team1Players.map((p) => p?.id).filter(Boolean),
                ...team2Players.map((p) => p?.id).filter(Boolean),
              ]),
            ].sort();

            const linksButton = (
              <button
                type="button"
                onClick={() => setExpandedGameId(expandedGameId === game.id ? null : game.id)}
                className="flex shrink-0 items-center gap-1 text-xs font-medium text-primary hover:text-primary-dark"
              >
                {linkCounts[game.id] !== undefined && linkCounts[game.id] > 0 && (
                  <span title={`${linkCounts[game.id]} link(s)`}>
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                      />
                    </svg>
                  </span>
                )}
                {expandedGameId === game.id ? 'Hide' : 'Links'}
                {linkCounts[game.id] !== undefined && linkCounts[game.id] > 0 && (
                  <span className="inline-flex min-w-[1.125rem] items-center justify-center rounded-full bg-primary px-1 py-px text-[10px] font-semibold leading-none text-text-inverse">
                    {linkCounts[game.id]}
                  </span>
                )}
              </button>
            );

            return (
              <React.Fragment key={game.id}>
                {isNewSession && !hideSessionHeaders && (
                  <div
                    className={`rounded-lg border border-border-medium bg-surface-subtle px-2.5 py-1.5 ${
                      index === 0 ? 'border-t-2 border-t-primary' : ''
                    }`}
                  >
                    <div className="text-sm font-semibold leading-tight text-text-primary">
                      {game.sessionName || 'Unknown Session'}
                    </div>
                    <div className="text-xs text-text-secondary">
                      {date} • {game.clubName || 'Unknown'}
                    </div>
                  </div>
                )}
                <div
                  className={`rounded-lg border border-border-medium px-2 py-2 sm:px-3 sm:py-2.5 lg:px-4 ${
                    isEvenSession ? 'bg-surface' : 'bg-surface-subtle/60'
                  }`}
                >
                  <div className="sm:hidden">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <span className="flex min-w-0 items-center gap-1.5">
                        <span className="tabular-nums text-text-muted">(#{game.gameNumber ?? '—'})</span>
                        {renderEditedBadge(game)}
                      </span>
                      {linksButton}
                    </div>
                    <div className="overflow-hidden rounded-lg border border-border-medium divide-y divide-border-medium">
                      <div className={`flex items-start justify-between gap-3 px-2.5 py-2 ${team1Class}`}>
                        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-0.5 text-xs leading-snug">
                          {renderSessionTeamPlayers(team1Players, eloByPlayerId, game, { wrap: true })}
                          {renderTeamAvgElo(game.team1AvgElo, game)}
                        </div>
                        <span className="shrink-0 tabular-nums text-base font-bold text-text-primary">
                          {team1Score}
                        </span>
                      </div>
                      <div className={`flex items-start justify-between gap-3 px-2.5 py-2 ${team2Class}`}>
                        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-0.5 text-xs leading-snug">
                          {renderSessionTeamPlayers(team2Players, eloByPlayerId, game, { wrap: true })}
                          {renderTeamAvgElo(game.team2AvgElo, game)}
                        </div>
                        <span className="shrink-0 tabular-nums text-base font-bold text-text-primary">
                          {team2Score}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="hidden sm:flex items-center gap-2 min-w-0 text-sm">
                    <span className="flex shrink-0 items-center gap-1.5">
                      <span className="w-9 text-right tabular-nums text-text-muted">
                        (#{game.gameNumber ?? '—'})
                      </span>
                      {renderEditedBadge(game)}
                    </span>
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <div
                        className={`flex min-w-0 max-w-[45%] lg:max-w-none lg:flex-initial items-center gap-x-1 rounded-full px-2 py-1 ${team1Class}`}
                      >
                        {renderSessionTeamPlayers(team1Players, eloByPlayerId, game)}
                        {renderTeamAvgElo(game.team1AvgElo, game)}
                      </div>
                      <span className="shrink-0 tabular-nums font-bold text-text-primary whitespace-nowrap text-base">
                        {team1Score}
                        <span className="mx-1 font-normal text-text-muted">-</span>
                        {team2Score}
                      </span>
                      <div
                        className={`flex min-w-0 flex-1 items-center gap-x-1 rounded-full px-2 py-1 ${team2Class}`}
                      >
                        {renderSessionTeamPlayers(team2Players, eloByPlayerId, game)}
                        {renderTeamAvgElo(game.team2AvgElo, game)}
                      </div>
                    </div>
                    <div className="pl-1">{linksButton}</div>
                  </div>
                  {expandedGameId === game.id && currentUserId && (
                    <div className="mt-2 border-t border-border-medium pt-2">
                      <div className="mx-auto max-w-2xl">
                        <GameContextLinks
                          gameId={game.id}
                          currentUserId={currentUserId}
                          clubId={clubId}
                          gameParticipantIds={stableParticipantIds}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </React.Fragment>
            );
          }

          const isTeam1 = game.team_number === 1;
          const team1Score = game.session_games?.team_1_score || 0;
          const team2Score = game.session_games?.team_2_score || 0;
          const userScore = isTeam1 ? team1Score : team2Score;
          const oppScore = isTeam1 ? team2Score : team1Score;
          const isWin = userScore > oppScore;
          const date =
            formattedDates[game.id] ||
            format(new Date(game.created_at || game.session_games?.created_at || 0), 'MMM d, yyyy');
          const teammates = game.teammates || [];
          const opponents = game.opponents || [];
          const currentSessionId = game.session_games?.session_id || game.session_id;
          const prevGame = index > 0 ? paginatedGames[index - 1] : null;
          const prevSessionId = prevGame?.session_games?.session_id || prevGame?.session_id;
          const isNewSession = index === 0 || (index > 0 && currentSessionId !== prevSessionId);
          const sessionGroup = sessionGroups[index];
          const isEvenSession = sessionGroup % 2 === 0;
          const gamePlayerId = game.player_id;
          const gameParticipantIds = [
            ...(teammates?.map((t) => t?.id).filter(Boolean) || []),
            ...(opponents?.map((o) => o?.id).filter(Boolean) || []),
            game.player_id,
          ].filter(Boolean);
          const stableParticipantIds = [...new Set(gameParticipantIds)].sort();

          return (
            <React.Fragment key={game.id}>
              {isNewSession && !hideSessionHeaders && (
                <div
                  className={`rounded-lg border border-border-medium bg-surface-subtle px-2.5 py-1.5 ${
                    index === 0 ? 'border-t-2 border-t-primary' : ''
                  }`}
                >
                  <div className="flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold leading-tight text-text-primary">
                        {game.sessionName || 'Unknown Session'}
                      </div>
                      <div className="text-xs text-text-secondary">
                        {date} • {game.clubName || 'Unknown'}
                      </div>
                    </div>
                    {currentSessionId && gamePlayerId && !hideViewSessionLink && (
                      <Button
                        variant="link"
                        size="small"
                        onClick={() => {
                          navigate(`/player/${gamePlayerId}/session/${currentSessionId}`, {
                            state: { clubId: game.club_id || clubId },
                          });
                        }}
                        className="shrink-0 self-start px-0 text-xs"
                      >
                        View Session →
                      </Button>
                    )}
                  </div>
                </div>
              )}
              <div
                className={`rounded-lg border border-border-medium px-2 py-2 sm:px-3 sm:py-2.5 lg:px-4 ${
                  isEvenSession ? 'bg-surface' : 'bg-surface-subtle/60'
                }`}
              >
                {(() => {
                  const linksButton = (
                    <button
                      type="button"
                      onClick={() => setExpandedGameId(expandedGameId === game.id ? null : game.id)}
                      className="flex shrink-0 items-center gap-1 text-xs font-medium text-primary hover:text-primary-dark"
                    >
                      {linkCounts[game.id] !== undefined && linkCounts[game.id] > 0 && (
                        <span title={`${linkCounts[game.id]} link(s)`}>
                          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                            />
                          </svg>
                        </span>
                      )}
                      {expandedGameId === game.id ? 'Hide' : 'Links'}
                      {linkCounts[game.id] !== undefined && linkCounts[game.id] > 0 && (
                        <span className="inline-flex min-w-[1.125rem] items-center justify-center rounded-full bg-primary px-1 py-px text-[10px] font-semibold leading-none text-text-inverse">
                          {linkCounts[game.id]}
                        </span>
                      )}
                    </button>
                  );

                  const yourTeamContent = (
                    <>
                      <span className="shrink-0 font-semibold text-text-primary">{subjectPlayerLabel}</span>
                      {teammates.length > 0 ? (
                        teammates.map((teammate, idx) => (
                          <React.Fragment key={teammate.id || idx}>
                            <span className="shrink-0 text-text-muted">+</span>
                            {renderPartnerName(teammate, { wrap: true })}
                          </React.Fragment>
                        ))
                      ) : (
                        <span className="shrink-0 text-text-muted">(solo)</span>
                      )}
                      {game.eloChange !== null &&
                        game.eloChange !== undefined &&
                        !hideEloRatings &&
                        game.game_type !== 'social' && (
                          <span className="shrink-0 ml-0.5">
                            <EloChange change={game.eloChange} showSign size="xs" />
                          </span>
                        )}
                      {renderTeamAvgElo(game.myTeamAvgElo, game)}
                    </>
                  );

                  const opponentContent =
                    opponents.length > 0 ? (
                      <>
                        {opponents.map((opponent, idx) => (
                          <React.Fragment key={opponent.id || idx}>
                            {idx > 0 ? <span className="shrink-0 text-text-muted">+</span> : null}
                            {renderPartnerName(opponent, { wrap: true })}
                          </React.Fragment>
                        ))}
                        {renderTeamAvgElo(game.oppTeamAvgElo, game)}
                      </>
                    ) : (
                      <span className="text-text-muted">Unknown</span>
                    );

                  return (
                    <>
                      {/* Mobile: scoreboard — each team on its own row with score on the right */}
                      <div className="sm:hidden">
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <span className="flex min-w-0 items-center gap-1.5">
                            <span className="tabular-nums text-text-muted">(#{game.gameNumber ?? '—'})</span>
                            {renderEditedBadge(game)}
                          </span>
                          {linksButton}
                        </div>
                        <div className="overflow-hidden rounded-lg border border-border-medium divide-y divide-border-medium">
                          <div
                            className={`flex items-start justify-between gap-3 px-2.5 py-2 ${
                              isWin ? YOUR_TEAM_WIN : YOUR_TEAM_LOSS
                            }`}
                          >
                            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-0.5 text-xs leading-snug">
                              {yourTeamContent}
                            </div>
                            <span className="shrink-0 tabular-nums text-base font-bold text-text-primary">
                              {userScore}
                            </span>
                          </div>
                          <div className={`flex items-start justify-between gap-3 px-2.5 py-2 ${OPPONENT_NEUTRAL}`}>
                            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-0.5 text-xs leading-snug">
                              {opponentContent}
                            </div>
                            <span className="shrink-0 tabular-nums text-base font-bold text-text-primary">
                              {oppScore}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* sm+: single-row layout */}
                      <div className="hidden sm:flex items-center gap-2 min-w-0 text-sm">
                        <span className="flex shrink-0 items-center gap-1.5">
                          <span className="w-9 text-right tabular-nums text-text-muted">
                            (#{game.gameNumber ?? '—'})
                          </span>
                          {renderEditedBadge(game)}
                        </span>

                        <div className="flex min-w-0 flex-1 items-center gap-3">
                          <div
                            className={`flex min-w-0 max-w-[45%] lg:max-w-none lg:flex-initial items-center gap-x-1 rounded-full px-2 py-1 ${
                              isWin ? YOUR_TEAM_WIN : YOUR_TEAM_LOSS
                            }`}
                            title={isWin ? `${subjectPlayerLabel} won` : `${subjectPlayerLabel} lost`}
                          >
                            <span className="shrink-0 font-semibold text-text-primary">{subjectPlayerLabel}</span>
                            {teammates.length > 0 ? (
                              teammates.map((teammate, idx) => (
                                <React.Fragment key={teammate.id || idx}>
                                  <span className="shrink-0 text-text-muted">+</span>
                                  {renderPartnerName(teammate)}
                                </React.Fragment>
                              ))
                            ) : (
                              <span className="shrink-0 text-text-muted">(solo)</span>
                            )}
                            {game.eloChange !== null &&
                              game.eloChange !== undefined &&
                              !hideEloRatings &&
                              game.game_type !== 'social' && (
                                <span className="shrink-0 ml-0.5">
                                  <EloChange change={game.eloChange} showSign size="xs" className="sm:text-sm" />
                                </span>
                              )}
                            {renderTeamAvgElo(game.myTeamAvgElo, game)}
                          </div>

                          <span className="shrink-0 tabular-nums font-bold text-text-primary whitespace-nowrap text-base">
                            {userScore}
                            <span className="mx-1 font-normal text-text-muted">-</span>
                            {oppScore}
                          </span>

                          <div
                            className={`flex min-w-0 flex-1 items-center gap-x-1 rounded-full px-2 py-1 ${OPPONENT_NEUTRAL}`}
                          >
                            {opponents.length > 0 ? (
                              opponents.map((opponent, idx) => (
                                <React.Fragment key={opponent.id || idx}>
                                  {idx > 0 ? <span className="shrink-0 text-text-muted">+</span> : null}
                                  {renderPartnerName(opponent)}
                                </React.Fragment>
                              ))
                            ) : (
                              <span className="text-text-muted">Unknown</span>
                            )}
                            {renderTeamAvgElo(game.oppTeamAvgElo, game)}
                          </div>
                        </div>

                        <div className="pl-1">{linksButton}</div>
                      </div>
                    </>
                  );
                })()}
                {expandedGameId === game.id && currentUserId && (
                  <div className="mt-2 border-t border-border-medium pt-2">
                    <div className="mx-auto max-w-2xl">
                      <GameContextLinks
                        gameId={game.id}
                        currentUserId={currentUserId}
                        clubId={clubId}
                        gameParticipantIds={stableParticipantIds}
                      />
                    </div>
                  </div>
                )}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="mt-4 space-y-3 border-t border-border-medium pt-4 lg:flex lg:items-center lg:justify-between lg:space-y-0">
          <div className="text-center text-xs text-text-muted sm:text-sm lg:text-left">
            Showing {startIndex + 1} to {Math.min(startIndex + pageSize, gamesWithNumbers.length)} of{' '}
            {gamesWithNumbers.length} games
          </div>
          {/* Compact controls on small screens — avoids horizontal overflow */}
          <div className="flex items-center justify-center gap-2 lg:hidden">
            <button
              type="button"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className={`rounded border px-3 py-2 text-sm font-medium ${
                currentPage === 1
                  ? 'cursor-not-allowed border-border bg-surface-subtle text-text-muted'
                  : 'border-border bg-surface-subtle text-text-secondary hover:bg-surface-elevated'
              }`}
            >
              Previous
            </button>
            <span className="min-w-[5.5rem] text-center text-sm text-text-secondary">
              Page {currentPage} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`rounded border px-3 py-2 text-sm font-medium ${
                currentPage === totalPages
                  ? 'cursor-not-allowed border-border bg-surface-subtle text-text-muted'
                  : 'border-border bg-surface-subtle text-text-secondary hover:bg-surface-elevated'
              }`}
            >
              Next
            </button>
          </div>
          <div className="hidden flex-wrap justify-end gap-1 lg:flex">
            <button
              type="button"
              onClick={() => goToPage(1)}
              disabled={currentPage === 1}
              className={`rounded border px-2 py-1 text-sm sm:px-3 ${
                currentPage === 1
                  ? 'cursor-not-allowed border-border bg-surface-subtle text-text-muted'
                  : 'border-border bg-surface-subtle text-text-secondary hover:bg-surface-elevated'
              }`}
            >
              &laquo;
            </button>
            <button
              type="button"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className={`rounded border px-2 py-1 text-sm sm:px-3 ${
                currentPage === 1
                  ? 'cursor-not-allowed border-border bg-surface-subtle text-text-muted'
                  : 'border-border bg-surface-subtle text-text-secondary hover:bg-surface-elevated'
              }`}
            >
              &lsaquo;
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <button
                  type="button"
                  key={i}
                  onClick={() => goToPage(pageNum)}
                  className={`rounded border px-2 py-1 text-sm sm:px-3 ${
                    currentPage === pageNum
                      ? 'border-primary bg-primary text-text-inverse'
                      : 'border-border bg-surface-subtle text-text-secondary hover:bg-surface-elevated'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`rounded border px-2 py-1 text-sm sm:px-3 ${
                currentPage === totalPages
                  ? 'cursor-not-allowed border-border bg-surface-subtle text-text-muted'
                  : 'border-border bg-surface-subtle text-text-secondary hover:bg-surface-elevated'
              }`}
            >
              &rsaquo;
            </button>
            <button
              type="button"
              onClick={() => goToPage(totalPages)}
              disabled={currentPage === totalPages}
              className={`rounded border px-2 py-1 text-sm sm:px-3 ${
                currentPage === totalPages
                  ? 'cursor-not-allowed border-border bg-surface-subtle text-text-muted'
                  : 'border-border bg-surface-subtle text-text-secondary hover:bg-surface-elevated'
              }`}
            >
              &raquo;
            </button>
          </div>
        </div>
      )}

      {latestSessionOnlyByDefault && hasMultipleSessions && (
        <div className="mt-4 border-t border-border-medium pt-3">
          <Button
            type="button"
            variant="ghost"
            size="small"
            className="h-auto min-h-0 w-full py-2 text-xs font-medium text-primary"
            aria-expanded={showFullHistory}
            onClick={() => setShowFullHistory((v) => !v)}
          >
            {showFullHistory
              ? 'Show latest session only'
              : `Show all sessions (${games.length} games)`}
          </Button>
        </div>
      )}
        </div>
      )}
    </div>
  );
};

export { GameHistoryTable };
export default GameHistoryTable; 